import React, { useState, useEffect, type FC } from 'react';

import { DateTime } from 'luxon';
import axios, { type AxiosResponse } from 'axios';

import { Box } from '@clutter/clean';

import { Panel, Table, NavLinkButton } from '@shared/components/bootstrap';
import { Pluralize } from '@shared/components/helpers';
import { UserRole } from '@shared/types/user_role';

import { SelectFormGroup } from '@admin/components/fields/select_form_group';
import { Roles } from '@admin/components/helpers/roles';
import { Pagination } from '@admin/components/pagination';
import { Spinner } from '@admin/components/spinner';
import { WarehouseSelectFormGroup } from '@admin/components/warehouses/select_form_group';
import { useAxiosRequest } from '@admin/hooks';
import { useTitle } from '@admin/hooks/use_title';
import { IPagination } from '@admin/types/pagination';

import { TransferStateLabel } from './state_label';

const DEFAULT_PAGE = 1;

type TransferState = 'approved' | 'enroute' | 'arrived' | 'completed' | 'canceled';

const STATES: TransferState[] = ['approved', 'enroute', 'arrived', 'completed', 'canceled'];

type User = {
  id: number;
  name: string;
};

type Warehouse = {
  id: number;
  name: string;
};
type Vehicle = {
  id: number;
  serial_code: string;
};

type Transfer = {
  id: number;
  user: User;
  origin: Warehouse;
  destination: Warehouse;
  vehicle?: Vehicle;
  cuft: number;
  pallets_count: number;
  pallet_height_counts: string;
  scheduled: string;
  state: TransferState;
};

type Paginated = {
  results: Transfer[];
  pagination: IPagination;
};

const useTransfersRequest = ({
  page,
  originID,
  destinationID,
  state,
}: {
  page: number;
  originID?: string;
  destinationID?: string;
  state?: TransferState;
}) => {
  const [request, setRequest] = useState<Promise<AxiosResponse<Paginated>> | undefined>();

  useEffect(() => {
    setRequest(
      axios.get<Paginated>(`/transfers.json`, {
        params: { page, origin_id: originID, destination_id: destinationID, state },
      }),
    );
  }, [page, originID, destinationID, state]);

  return useAxiosRequest(request);
};

const TransferStateSelectFormGroup: FC<{
  placeholder?: string;
  value: TransferState | undefined;
  onChange(state: TransferState | undefined): void;
}> = ({ placeholder = ' - Status - ', value, onChange }) => (
  <SelectFormGroup
    value={value ?? ''}
    onChange={(event) => onChange((event.target.value || undefined) as TransferState | undefined)}
  >
    <option value="">{placeholder}</option>
    {STATES.map((state) => (
      <option key={state} value={state}>
        {state}
      </option>
    ))}
  </SelectFormGroup>
);

export const TransferList: FC = () => {
  useTitle('Transfers');
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [state, setState] = useState<TransferState | undefined>(undefined);
  const [originID, setOriginID] = useState<string | undefined>(undefined);
  const [destinationID, setDestinationID] = useState<string | undefined>(undefined);

  const { response, loading } = useTransfersRequest({ page, originID, destinationID, state });
  const pagination = response?.data.pagination;
  const transfers = response?.data.results;

  return (
    <Panel>
      <Panel.Body>
        <Box.Flex gap="4px" alignItems="center" justifyContent="space-between">
          <Box.Flex gap="4px">
            <WarehouseSelectFormGroup
              placeholder="- Origin -"
              value={originID}
              onChange={(value) => {
                setPage(DEFAULT_PAGE);
                setOriginID(value);
              }}
            />
            <WarehouseSelectFormGroup
              placeholder="- Destination -"
              value={destinationID}
              onChange={(value) => {
                setPage(DEFAULT_PAGE);
                setDestinationID(value);
              }}
            />
            <TransferStateSelectFormGroup
              placeholder="- Status -"
              value={state}
              onChange={(value) => {
                setPage(DEFAULT_PAGE);
                setState(value);
              }}
            />
          </Box.Flex>
          <NavLinkButton kind="default" to="/transferpools">
            View Transfer Lists
          </NavLinkButton>
        </Box.Flex>
        <Table responsive>
          <thead>
            <tr>
              <th className="col-zero col-nowrap">ID</th>
              <th className="col-sm-2 col-nowrap text-left">Created On</th>
              <th className="col-sm-2 col-nowrap text-left">Created By</th>
              <th className="col-sm-2 col-nowrap text-left">Origin Warehouse</th>
              <th className="col-sm-2 col-nowrap text-left">Destination Warehouse</th>
              <th className="col-sm-2 col-nowrap text-left">Vehicle</th>
              <th className="col-sm-2 col-nowrap text-left">Content Quantity</th>
              <th className="col-zero col-nowrap">Status</th>
              <th className="col-zero col-nowrap"></th>
            </tr>
          </thead>
          <tbody>
            {transfers?.map((transfer) => (
              <tr key={transfer.id}>
                <td className="col-zero col-nowrap">{transfer.id}</td>
                <td className="col-sm-2 col-nowrap text-left">
                  {DateTime.fromISO(transfer.scheduled).toLocaleString(DateTime.DATETIME_MED)}
                </td>
                <td className="col-sm-2 col-nowrap text-left">{transfer.user.name}</td>
                <td className="col-sm-2 col-nowrap text-left">{transfer.origin.name}</td>
                <td className="col-sm-2 col-nowrap text-left">{transfer.destination.name}</td>
                <td className="col-sm-2 col-nowrap text-left">{transfer.vehicle?.serial_code ?? '-'}</td>
                <td className="col-sm-2 col-nowrap text-left">
                  <div>
                    <Pluralize singular="pallet" plural="pallets" count={transfer.pallets_count} />
                    <br />
                    <span>{transfer.pallet_height_counts}</span>
                    <br />
                    <span>{transfer.cuft} Cuft</span>
                  </div>
                </td>
                <td className="col-zero col-nowrap">
                  <TransferStateLabel transfer={transfer} />
                </td>
                <td className="col-zero col-nowrap">
                  <NavLinkButton kind="primary" to={`/transfers/${transfer.id}`}>
                    View
                  </NavLinkButton>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        {loading && <Spinner />}
        {pagination && <Pagination pagination={pagination} onPage={setPage} />}
      </Panel.Body>
      <Roles show={[UserRole.Admin, UserRole.Manager]}>
        <Panel.Footer align="right">
          <NavLinkButton to="/transferables" kind="mint">
            Request Pallet Transfer
          </NavLinkButton>
        </Panel.Footer>
      </Roles>
    </Panel>
  );
};
